/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useEffect } from "react";
import { Outlet, useSearchParams, useLocation } from "react-router-dom";
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";
import "./App.css";
import { useTranslation } from "react-i18next";
import PerformanceMonitor from "./components/performance/PerformanceMonitor";
import CookieConsent from "./components/common/CookieConsent";
import TechSupportChat from "./components/common/TechSupportChat";

import ErrorBoundary from "./ErrorBoundary";
import { SupportChatProvider } from "./context/SupportChatProvider";

import { initWebVitals } from "./utils/performance";
import { isDeskshare, isProductSupportPath } from "./utils/siteConfig";
import { isStandalonePath, stripLanguagePrefix } from "./utils/localizedPath";

initWebVitals();
const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const isStandalone = searchParams.get("standalone") === "true";
  // Auth pages (/auth and all sub-routes) are self-contained — they must not
  // render the site header or footer. Detect by stripping any language prefix
  // (e.g. /de/auth/callback → /auth/callback) before comparing.
  //
  // V-8 item 2: this used the third and last private copy of the
  // language-prefix rule, and it was the only one without a word boundary, so
  // "/esports" stripped to "ports". One shared helper now, in
  // utils/localizedPath.ts.
  const isAuthPage = stripLanguagePrefix(location.pathname).startsWith("/auth");
  // AL-3 / R5 (Session 447): a page that brings its own minimal chrome. The
  // list lives in utils/localizedPath.ts and main.tsx nests the same routes
  // under StandaloneLayout, so the routing table and this suppression cannot
  // disagree about which pages are standalone.
  const isStandalonePage = isStandalonePath(location.pathname);
  const hideSiteChrome = isStandalone || isAuthPage || isStandalonePage;

  // T4: the widget belongs to Deskshare-operated hosts. isDeskshare()
  // excludes shop.deskshare.com and api.deskshare.com, which have their own
  // routing and must not show this widget. It does NOT restrict the widget to
  // the marketing site alone — staging, feature environments and local
  // development all return true, which is correct: those are where it is
  // tested. Do not rely on it for a marketing-only guarantee.
  //
  // V-8 item 1: the widget is scoped to the product support page, in
  // any language. isProductSupportPath() accepts both /product-support and
  // /product-support/, because the site links to it both ways and the router
  // treats them as one route. /priority-support redirects here, so it is
  // covered too. The temporary site-wide hide in techSupportChat.css that used
  // to make this line moot has been deleted.
  const showTechSupportChat =
    !hideSiteChrome && isProductSupportPath(location.pathname) && isDeskshare();

  // Keep the document language in sync with the active locale on every page,
  // including standalone (embedded) views that do not render the header. The
  // header otherwise sets <html lang> via Helmet, but standalone pages skip it,
  // leaving screen readers and search engines with the wrong language.
  useEffect(() => {
    if (i18n.language) {
      document.documentElement.lang = i18n.language;
    }
  }, [i18n.language]);

  return (
    // The provider wraps the whole tree, above the outlet, so the support form
    // inside the outlet and the widget beside it see the same context.
    <SupportChatProvider>
      <div className="page">
        {!hideSiteChrome && (
          <a href="#main-content" className="skip-to-content">
            {t("accessibility.skip_to_content")}
          </a>
        )}
        {!hideSiteChrome && <Header />}

        {/* Performance monitor (only visible in development) */}
        <PerformanceMonitor />

        {/* GDPR: CookieConsent gates Google Analytics and Google Ads. Shows
          once until the user accepts or declines -- except on a
          ?standalone=true page (Session 515: the policy windows opened from a
          purchase), where it opens only when asked. See CookieConsent.tsx. */}
        <CookieConsent showBanner={!isStandalone} />

        {/* Item 4 (Session 361): the footer moves outside this wrapper, and
          the chat widget moves inside it.

          The launcher used to be pinned to the window, so on a short page —
          which the product support page is — it sat a blue pill on top of the
          black footer bar, and on narrow windows it covered the footer's own
          links. The fix needs no measuring: the launcher now rides in a
          zero-height sticky dock that ends where the footer begins, so the
          browser keeps it clear of the footer by itself. That only works if
          the footer is a sibling of this wrapper rather than inside it. */}
        <div className="page-body">
          <main id="main-content">
            <Outlet />
          </main>

          {/* Deskshare Support launcher + panel.
          Shown on the product support page only. Hidden on auth pages and
          standalone (embedded) views, and on hosts that are not
          Deskshare-operated (T4). The CookieConsent banner sits above it
          (z-index ~9999 > 9000).

          V-8 item 6: the boundary is the only one covering the application
          shell — every routed page is wrapped by its lazy loader, but the
          shell around them is not, and there is no boundary at the root
          either, so an error here would unmount the whole application and
          leave a blank page on the one page a customer reaches when something
          has already gone wrong for them.

          fallback={null} is deliberate. The boundary's default fallback is a
          bordered "Something went wrong" card marked as an urgent alert; for a
          floating widget that would interrupt the customer mid-task about a
          failure they did not need to know about. With an empty fallback the
          widget simply is not there, the form is untouched, the nudge
          disappears with it (chatAvailable goes false), and the error still
          reaches Sentry through the boundary's own reporting. */}
          {showTechSupportChat && (
            <ErrorBoundary fallback={null}>
              <TechSupportChat />
            </ErrorBoundary>
          )}
        </div>

        {!hideSiteChrome && <Footer />}
      </div>
    </SupportChatProvider>
  );
};

export default App;
